import * as XLSX from "xlsx";
import type { ParsedUpload, SlotDef } from "@ranfac/shared";

export async function parseFileForSlot(
  file: File,
  def: SlotDef,
): Promise<ParsedUpload> {
  const buf = await file.arrayBuffer();
  const name = file.name.toLowerCase();
  const ext = name.split(".").pop() ?? "";

  if (!def.expectedExtensions.includes(ext as "xlsx" | "csv")) {
    throw new Error(
      `Expected ${def.expectedExtensions.join(" or ")} for ${def.name}; got .${ext}.`,
    );
  }

  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  const sheetName = pickSheet(wb, def);
  const ws = wb.Sheets[sheetName];
  if (!ws) throw new Error(`Worksheet "${sheetName}" not found in file.`);

  const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {
    raw: true,
    defval: null,
  });

  // Trim any trailing footer rows (a row where first column is empty but
  // some numeric column is populated — the "SUM" rows TJM adds manually).
  const rows = raw.filter((r) => {
    const vals = Object.values(r);
    const nonEmpty = vals.filter((v) => v !== null && v !== "").length;
    return nonEmpty >= 3;
  });

  return {
    slotId: def.id,
    filename: file.name,
    rows: rows.map(coerceRow),
  };
}

function pickSheet(wb: XLSX.WorkBook, def: SlotDef): string {
  if (def.sheetName) {
    const match = wb.SheetNames.find((n) =>
      n.toLowerCase().includes(def.sheetName!.toLowerCase()),
    );
    if (match) return match;
  }
  return wb.SheetNames[0] ?? "Sheet1";
}

function coerceRow(
  r: Record<string, unknown>,
): Record<string, string | number | null> {
  const out: Record<string, string | number | null> = {};
  for (const [k, v] of Object.entries(r)) {
    if (v === null || v === undefined || v === "") {
      out[k] = null;
    } else if (v instanceof Date) {
      out[k] = v.toISOString().slice(0, 10);
    } else if (typeof v === "number" || typeof v === "string") {
      out[k] = v;
    } else {
      out[k] = String(v);
    }
  }
  return out;
}
